import * as React from "react";

import type {
  BedrockCatalogModel,
  BulkOrgModelConfigResponse,
} from "@/shared/domain/orgs";
import { Checkbox, RobotoButton } from "@/shared/foundation/primitives";

import styles from "./AiModels.module.css";
import { BulkActionApply, BulkResultSummary } from "./BulkActionApply";
import { OrgModelConfigTable } from "./OrgModelConfigTable";

/** Whether the panel adds the model to the picked orgs or takes it off them. */
export type OrgModelPanelMode = "add" | "remove";

interface MakeAvailablePanelProps {
  /** The chat model to add to (a verified one) or remove from orgs. */
  model: BedrockCatalogModel;
  /** The whole catalog, for the org table's "Has model" filter. */
  catalog: readonly BedrockCatalogModel[];
  mode: OrgModelPanelMode;
  onClose: () => void;
}

/**
 * Add one catalog model to orgs, or remove it from them: pick them in the
 * same server-filtered org table as the Orgs view, choose (when adding)
 * whether it becomes their default, apply, and see the result. Rendered in
 * the page, not a dialog, because the table's filter menu is a popover,
 * which layers below dialogs.
 */
export function MakeAvailablePanel({
  model,
  catalog,
  mode,
  onClose,
}: MakeAvailablePanelProps) {
  const titleId = React.useId();
  const [makeDefault, setMakeDefault] = React.useState(false);
  const [applied, setApplied] =
    React.useState<BulkOrgModelConfigResponse | null>(null);
  const addSkipNote = makeDefault
    ? "Orgs where it is already the default are skipped."
    : "Orgs that already have it are skipped.";

  return (
    <section className={styles.panel} aria-labelledby={titleId}>
      <div className={styles.catalogHeader}>
        <h3 id={titleId} className={styles.sectionTitle}>
          {mode === "add"
            ? `Make ${model.label} available to orgs`
            : `Remove ${model.label} from orgs`}
        </h3>
        <RobotoButton variant="ghost" size="sm" onClick={onClose}>
          Close
        </RobotoButton>
      </div>
      <p className={styles.note}>
        {mode === "add"
          ? `Each org gets the model added to its list; models on its own connections are kept. ${addSkipNote}`
          : "The model is taken off each org's list. Where it was the default, the next chat model on the list becomes the default. Orgs without it, or with no other chat model, are skipped."}
      </p>
      {applied !== null && (
        <BulkResultSummary result={applied} verb="Updated" />
      )}
      <OrgModelConfigTable
        compact
        chatModels={catalog}
        renderActions={(orgIds, clearTarget) => (
          <div className={styles.panelActions}>
            {mode === "add" && (
              <Checkbox
                label="Make it the default"
                checked={makeDefault}
                onCheckedChange={setMakeDefault}
              />
            )}
            <BulkActionApply
              orgIds={orgIds}
              action={
                mode === "add"
                  ? {
                      type: "add_model",
                      profile_id: model.id,
                      make_default: makeDefault,
                    }
                  : { type: "remove_model", profile_id: model.id }
              }
              onApplied={(result) => {
                setApplied(result);
                clearTarget();
              }}
            />
          </div>
        )}
      />
    </section>
  );
}
